import { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Grid2 as Grid,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  TextField,
  Checkbox,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";

const ProcessDocumentModal = ({ open, onClose, document }) => {
  const signerTypes = []
  const signersDetails = []
  const imSigner = null
  const instructionText = "Please review the information below and select the signing order for the documents. The signing order determines the sequence in which the signers will receive the documents for signing."
  const contractorSigning = {}
  const ownerSigning = {}
  const selectedRow = {}

  const [signingOrder, setSigningOrder] = useState("contractor");
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [mobileConsent, setMobileConsent] = useState(false);

  const [notarizeDocument, setNotarizeDocument] = useState(false);
  const [recordDocument, setRecordDocument] = useState(false);

  useEffect(() => {
    if (document?.document?.actions?.notarize) {
      setNotarizeDocument(true);
    } else {
      setNotarizeDocument(false);
    }
    if (document?.document?.actions?.record) {
      setRecordDocument(true);
    } else {
      setRecordDocument(false);
    }
  }, [open, document?.document?.actions?.notarize, document?.document?.actions?.record]);

  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])/g, " $1") // Add space before caps
      .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
  };

  const disabledIm = [
    "companyName",
    "ownerNameType",
    "firstName",
    "middleName",
    "lastName",
    "county",
  ];
  const signerFieldOrder = [
    "firstName",
    "middleName",
    "lastName",
    "companyName",
    "email",
    "phone",
  ];

  return (
    <Modal open={open} onClose={onClose} sx={{ overflow: "auto" }}>
      <Box
        sx={{
          overflowY: "none",
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90%",
          maxWidth: "70rem",
          bgcolor: "background.paper",
          borderRadius: "18px",
          boxShadow: 24,
          px: 4,
          py: 2,
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <Typography
          id="modal-modal-title"
          variant="h4"
          align="center"
          component="h2"
          color="primary"
        >
          {notarizeDocument && recordDocument
            ? "Notarize & Record Your Notice of Commencement"
            : notarizeDocument
            ? "Notarize Your Notice of Commencement"
            : "Record Your Notice of Commencement"}
        </Typography>
        <Box sx={{ position: "absolute", right: 10, top: 10 }}>
          <IconButton
            onClick={() => {
              onClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography
          color="primary"
          align="center"
          id="modal-modal-description"
          sx={{ fontWeight: "bold", mb: 0 }}
        >
          Process your documents has never been so easy
        </Typography>
        <Typography
          align="center"
          variant="body1"
          fontWeight="bold"
          sx={{ color: "#f79009" }}
        ></Typography>

        <Box
          sx={{
            maxHeight: "35.3rem", // Adjust the max height as needed
            overflowY: "auto",
            height: "calc(90vh - 350px)",
            border: "2px solid #d3d3d3",
            mb: 9,
            p: 2,
            borderRadius: "18px",
            boxShadow: "3px 4px 12px rgba(0, 0, 0, 0.3)", // Added box shadow
          }}
        >
          {/* Signing Order Selection */}
          <Box sx={{ mb: 1, borderBottom: "3px solid #f3f3f3" }}>
            <Typography
              align="center"
              variant="body2"
              color="primary"
              fontWeight="bold"
            >
              {instructionText}
            </Typography>
            {contractorSigning?.notarizationNeeded ||
            selectedRow?.assigned_package?.need_assign ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <FormControl
                  component="fieldset"
                  sx={{ width: "100%" }}
                >
                  <FormLabel
                    component="legend"
                    sx={{
                      fontWeight: "bold",
                      mb: -1,
                      color: "text.secondary",
                      width: "100%",
                      textAlign: "center",
                    }}
                    focused={false}
                  >
                    Select Signing Order
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-label="signing-order"
                    name="signing-order"
                    value={signingOrder}
                    onChange={(e) => setSigningOrder(e.target.value)}
                    sx={{ justifyContent: "center" }}
                  >
                    <FormControlLabel
                      disabled={
                        selectedRow?.documents?.noc_affidavit?.is_created ===
                        true
                      }
                      value="contractor"
                      control={<Radio />}
                      label="Contractor First"
                    />
                    <FormControlLabel
                      value="owner"
                      control={<Radio />}
                      label="Owner/Agent First"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            ) : (
              <></>
            )}
          </Box>

          {signerTypes.includes("sign_only") && (
            <>
              <Typography align="center" color="primary" variant="h5">
                Overview of the signer(s)
              </Typography>
              <Grid container spacing={1} mt={2} mb={2}>
                <Grid
                  size={{ xs: 6 }}
                  sx={{ borderRight: "2px solid #d3d3d3" }}
                  pr={1}
                >
                  <Typography fontWeight="bold" align="center" mb={2}>
                    Your Signing Details
                  </Typography>

                  {imSigner && Object.keys(imSigner).length > 0 ? (
                    <>
                      <Typography fontWeight="bold" color="primary" mb={1}>
                        {imSigner.signer
                          ?.replace(/_/g, " ")
                          .replace(/^./, (c) => c.toUpperCase()) || "Signer"}
                      </Typography>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {Object.entries(imSigner)
                          .filter(
                            ([key]) =>
                              ![
                                "signer",
                                "im",
                                "ownerNameType",
                                "county",
                              ].includes(key)
                          )
                          .sort(([a], [b]) => {
                            const indexA = signerFieldOrder.indexOf(a);
                            const indexB = signerFieldOrder.indexOf(b);
                            // If not in the list, push to the end
                            return (
                              (indexA === -1 ? 999 : indexA) -
                              (indexB === -1 ? 999 : indexB)
                            );
                          })
                          .map(([key, value], index) => (
                            <TextField
                              disabled={disabledIm.includes(key)}
                              key={index}
                              label={formatLabel(key)}
                              value={value}
                              name={key}
                              size="medium"
                              variant="filled"
                              sx={{ width: "calc(50% - 0.5rem)" }}
                              onChange={() => {}}
                            />
                          ))}
                      </Box>
                    </>
                  ) : (
                    <Typography
                      fontWeight="bold"
                      align="center"
                      mb={2}
                      color="primary"
                    >
                      You do not required to sign document(s)
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 6 }} pl={1}>
                  <Typography fontWeight="bold" align="center" mb={2}>
                    Other Signers
                  </Typography>
                  {signersDetails && signersDetails.length > 0 ? (
                    signersDetails.map((signerObj, signerIndex) => {
                      const signerId = signerObj.signer; // e.g. "signer_2"
                      const title = signerId
                        .replace(/_/g, " ")
                        .replace(/^./, (c) => c.toUpperCase()); // "signer_2" -> "Signer 2"

                      const signerFields = Object.entries(signerObj)
                        .filter(
                          ([key]) =>
                            ![
                              "signer",
                              "im",
                              "ownerNameType",
                              "county",
                            ].includes(key)
                        )
                        .sort(([a], [b]) => {
                          const indexA = signerFieldOrder.indexOf(a);
                          const indexB = signerFieldOrder.indexOf(b);
                          return (
                            (indexA === -1 ? 999 : indexA) -
                            (indexB === -1 ? 999 : indexB)
                          );
                        });

                      return (
                        <Box key={signerIndex} mb={3}>
                          <Typography fontWeight="bold" color="primary" mb={1}>
                            {title}
                          </Typography>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {signerFields.map(([key, value], index) => (
                              <TextField
                                disabled={disabledIm.includes(key)}
                                key={index}
                                label={formatLabel(key)}
                                value={value}
                                name={key}
                                size="medium"
                                variant="filled"
                                sx={{ width: "calc(50% - 0.5rem)" }}
                                onChange={() => {}}
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography
                      fontWeight="bold"
                      align="center"
                      mb={2}
                      color="primary"
                    >
                      No other signers required to sign document(s)
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {signerTypes.includes("record_only") && (
            <>
              <Typography align="center" color="primary" variant="h5">
                Overview of Recording
              </Typography>
              <Grid container spacing={1} mt={2} mb={2}>
                <Grid
                  size={{ xs: 6 }}
                  sx={{ borderRight: "2px solid #d3d3d3" }}
                  pr={1}
                >
                  <Typography fontWeight="bold" align="center" mb={2}>
                    Grantor
                  </Typography>

                  {imSigner && Object.keys(imSigner).length > 0 ? (
                    <>
                      <Typography fontWeight="bold" color="primary" mb={1}>
                        {imSigner.signer
                          ?.replace(/_/g, " ")
                          .replace(/^./, (c) => c.toUpperCase()) || "Signer"}
                      </Typography>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {Object.entries(imSigner)
                          .filter(
                            ([key]) =>
                              ![
                                "signer",
                                "im",
                                "ownerNameType",
                                "county",
                              ].includes(key)
                          )
                          .sort(([a], [b]) => {
                            const indexA = signerFieldOrder.indexOf(a);
                            const indexB = signerFieldOrder.indexOf(b);
                            // If not in the list, push to the end
                            return (
                              (indexA === -1 ? 999 : indexA) -
                              (indexB === -1 ? 999 : indexB)
                            );
                          })
                          .map(([key, value], index) => (
                            <TextField
                              disabled={disabledIm.includes(key)}
                              key={index}
                              label={formatLabel(key)}
                              value={value}
                              name={key}
                              size="medium"
                              variant="filled"
                              sx={{ width: "calc(50% - 0.5rem)" }}
                              onChange={() => {}}
                            />
                          ))}
                      </Box>
                    </>
                  ) : (
                    <Typography
                      fontWeight="bold"
                      align="center"
                      mb={2}
                      color="primary"
                    >
                      You do not required to sign document(s)
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 6 }} pl={1}>
                  <Typography fontWeight="bold" align="center" mb={2}>
                    Other Signers
                  </Typography>
                  {signersDetails && signersDetails.length > 0 ? (
                    signersDetails.map((signerObj, signerIndex) => {
                      const signerId = signerObj.signer; // e.g. "signer_2"
                      const title = signerId
                        .replace(/_/g, " ")
                        .replace(/^./, (c) => c.toUpperCase()); // "signer_2" -> "Signer 2"

                      const signerFields = Object.entries(signerObj)
                        .filter(
                          ([key]) =>
                            ![
                              "signer",
                              "im",
                              "ownerNameType",
                              "county",
                            ].includes(key)
                        )
                        .sort(([a], [b]) => {
                          const indexA = signerFieldOrder.indexOf(a);
                          const indexB = signerFieldOrder.indexOf(b);
                          return (
                            (indexA === -1 ? 999 : indexA) -
                            (indexB === -1 ? 999 : indexB)
                          );
                        });

                      return (
                        <Box key={signerIndex} mb={3}>
                          <Typography fontWeight="bold" color="primary" mb={1}>
                            {title}
                          </Typography>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {signerFields.map(([key, value], index) => (
                              <TextField
                                disabled={disabledIm.includes(key)}
                                key={index}
                                label={formatLabel(key)}
                                value={value}
                                name={key}
                                size="medium"
                                variant="filled"
                                sx={{ width: "calc(50% - 0.5rem)" }}
                                onChange={() => {}}
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography
                      fontWeight="bold"
                      align="center"
                      mb={2}
                      color="primary"
                    >
                      No other signers required to sign document(s)
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {/* Declaration Section */}
          <Box
            sx={{ mt: 2, pt: 2, ml: 2, mr: 2, borderTop: "2px solid #d3d3d3" }}
          >
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={isAcknowledged}
                    onChange={(event) =>
                      setIsAcknowledged(event.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    I acknowledge that once notarization/recording begins, no
                    further modifications can be made to the documents.
                  </Typography>
                }
              />
            </Box>
            <Box mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="oMobileConsent"
                    checked={mobileConsent}
                    onChange={(e) => {
                      setMobileConsent(e.target.checked);
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm I have permission to use the provided email and
                    phone number to send messages related to this application.
                    Personal data will be processed in accordance with our{" "}
                    <Link
                      href="https://permitrockstar.com/privacy-policy/" // Replace with your actual Privacy Policy link
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      sx={{
                        textDecoration: "underline",
                        fontWeight: "bold",
                      }}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                }
              />
            </Box>
          </Box>
        </Box>

        {/* Submit Button */}
        <Box
          sx={{
            borderBottomLeftRadius: "18px",
            borderBottomRightRadius: "18px",
            backgroundColor: "#fff",
            position: "fixed",
            bottom: -20,
            left: 0,
            width: "100%",
            // borderTop: "3px solid #d3d3d3",
            py: 1,
            animation: "slideUp 0.5s ease-in-out",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", pb: 1 }}>
            <LoadingButton
              disabled={!isAcknowledged || !mobileConsent}
              color="success"
              variant="contained"
              sx={{ width: "16rem" }}
            >
              {!selectedRow?.payments?.vip?.paid &&
              !ownerSigning.notarizationNeeded &&
              !contractorSigning.notarizationNeeded
                ? "Submit Document(s) Signing"
                : !selectedRow?.payments?.vip?.paid &&
                  (ownerSigning.notarizationNeeded ||
                    contractorSigning.notarizationNeeded)
                ? "Submit Notarization Request"
                : "Submit Purchase"}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ProcessDocumentModal;
